import { useSearchParams } from 'react-router-dom'
import { useFeatureTimeline, useFeatures, useReleaseBands } from '../lib/api-client'
import { LoadingCard } from '../components/LoadingSpinner'
import { FeatureBreakdownChart } from '../components/charts/FeatureBreakdownChart'
import { FeatureTimelineChart } from '../components/charts/FeatureTimelineChart'
import { exportToCSV, exportToJSON } from '../lib/export'
import { useDateRange } from '../components/Layout'
import { ChartCard } from '../components/ui'

export function Features() {
  const { startDate, endDate } = useDateRange()
  const [searchParams] = useSearchParams()
  const features = useFeatures(startDate, endDate)
  const timeline = useFeatureTimeline(startDate, endDate, 'day')
  const releaseBands = useReleaseBands(startDate, endDate)
  const launchDate =
    searchParams.get('launchDate') || searchParams.get('launch_date') || undefined

  const handleExportCSV = () => {
    if (features.data?.data) {
      exportToCSV(features.data.data, 'features-breakdown')
    }
  }

  const handleExportJSON = () => {
    if (features.data?.data) {
      exportToJSON(features.data.data, 'features-breakdown')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-foreground">Feature Usage</h2>
        <div className="flex gap-2">
          <button
            onClick={handleExportCSV}
            disabled={!features.data}
            className="px-3 py-1 text-sm bg-secondary rounded-lg hover:bg-secondary/80 disabled:opacity-50"
          >
            Export CSV
          </button>
          <button
            onClick={handleExportJSON}
            disabled={!features.data}
            className="px-3 py-1 text-sm bg-secondary rounded-lg hover:bg-secondary/80 disabled:opacity-50"
          >
            Export JSON
          </button>
        </div>
      </div>

      <ChartCard title="Feature Usage Over Time">
        {timeline.isLoading ? (
          <LoadingCard />
        ) : timeline.data?.data.length ? (
          <FeatureTimelineChart
            data={timeline.data.data}
            releaseBands={releaseBands.data?.data}
            launchDate={launchDate}
          />
        ) : (
          <p className="text-muted-foreground">No data available</p>
        )}
      </ChartCard>

      {/* Chart */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold mb-4">Feature/Action Breakdown</h3>
        {features.isLoading ? (
          <LoadingCard />
        ) : features.data ? (
          <div className="h-[400px]">
            <FeatureBreakdownChart data={features.data.data} />
          </div>
        ) : (
          <p className="text-muted-foreground">No data available</p>
        )}
      </div>

      {/* Table */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <table className="w-full">
          <thead className="bg-secondary/50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                Feature
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                Action
              </th>
              <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">
                Count
              </th>
              <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">
                %
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {features.isLoading ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center">
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                  </div>
                </td>
              </tr>
            ) : features.data?.data.length ? (
              features.data.data.map((row, i) => (
                <tr key={i} className="hover:bg-secondary/30">
                  <td className="px-4 py-3 text-sm font-medium">
                    {row.feature}
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {row.action}
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    {row.count.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-muted-foreground">
                    {row.percentage.toFixed(1)}%
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-8 text-center text-muted-foreground"
                >
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
